import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, Row, Col, Typography, Button, Badge, Empty, Spin, Alert, Modal } from 'antd';
import {
  CheckCircleOutlined,
  CalendarOutlined,
  UserOutlined,
  HeartOutlined,
  MedicineBoxOutlined,
  ThunderboltOutlined,
  WomanOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { useTheme } from '../../components/ParticleBackground';
import useResponsive from '../../hooks/useResponsive';
import ServicesHero from './components/ServicesHero';
import CallToAction from './components/CallToAction';
import { servicesDB, categoriesDB } from '../../services/firebase/database';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import './Services.css';

const { Title, Text } = Typography;

const Services = () => {
  const { theme, getThemeStyles } = useTheme();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const { isMobile } = useResponsive();

  // State management
  const [activeTab, setActiveTab] = useState('');
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [mobileDetailVisible, setMobileDetailVisible] = useState(false);
  const [servicesData, setServicesData] = useState([]);
  const [serviceGroups, setServiceGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Icon mapping function
  const getServiceIcon = (service) => {
    const iconMap = {
      'YONI AROGYA': <WomanOutlined style={{ fontSize: '24px', color: '#e91e63' }} />,
      'SWASTHA AROGYA': <HeartOutlined style={{ fontSize: '24px', color: '#2196f3' }} />,
      'GARBHINI AROGYA': <UserOutlined style={{ fontSize: '24px', color: '#ff9800' }} />,
      'GARBHA SANSKAR': <UserOutlined style={{ fontSize: '24px', color: '#9c27b0' }} />,
      'NARI AROGYA CLASSES': <UserOutlined style={{ fontSize: '24px', color: '#4caf50' }} />,
      'DR CONSULTATION': <MedicineBoxOutlined style={{ fontSize: '24px', color: '#795548' }} />,
      'DR CONSULTATION WITH DIET CHART': <CalendarOutlined style={{ fontSize: '24px', color: '#607d8b' }} />,
      'NATUROPATHY DIAGNOSIS': <UserOutlined style={{ fontSize: '24px', color: '#ff5722' }} />,
      'ACUPUNCTURE': <UserOutlined style={{ fontSize: '24px', color: '#3f51b5' }} />,
      'WEEKEND SPECIALS': <ThunderboltOutlined style={{ fontSize: '24px', color: '#8bc34a' }} />,
    };

    if (service.title && iconMap[service.title.toUpperCase()]) {
      return iconMap[service.title.toUpperCase()];
    }

    const categoryIcons = {
      'Consultation': <MedicineBoxOutlined style={{ fontSize: '24px', color: '#795548' }} />,
      'Online yoga': <UserOutlined style={{ fontSize: '24px', color: '#4caf50' }} />,
      'Programs': <HeartOutlined style={{ fontSize: '24px', color: '#2196f3' }} />,
      'Specials': <ThunderboltOutlined style={{ fontSize: '24px', color: '#8bc34a' }} />,
      'Women & Pregnancy': <WomanOutlined style={{ fontSize: '24px', color: '#e91e63' }} />
    };

    return categoryIcons[service.category] || <UserOutlined style={{ fontSize: '24px', color: '#666' }} />;
  };

  // Tab icon mapping
  const getTabIcon = (groupId) => {
    const iconMap = {
      consultation: <MedicineBoxOutlined />,
      specials: <ThunderboltOutlined />,
      'women-pregnancy': <WomanOutlined />,
      programs: <HeartOutlined />
    };
    return iconMap[groupId] || <UserOutlined />;
  };

  // Get consistent theme styles from context
  const themeStyles = getThemeStyles();

  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [services, categories] = await Promise.all([
        servicesDB.getAll(),
        categoriesDB.getAll()
      ]);

      const transformedServices = services.map(service => ({
        ...service,
        id: parseInt(service.id) || service.id,
        icon: getServiceIcon(service),
        // Ensure price is available from options if not set directly
        price: service.price || (service.options && service.options.length > 0 ? parseInt(service.options[0].price) : 0)
      }));

      setServicesData(transformedServices);
      setServiceGroups(categories);

      // Set default active tab and select first course
      if (categories.length > 0) {
        const firstTab = categories[0].id;
        setActiveTab(firstTab);

        // Find first course in first tab
        const firstTabCourses = transformedServices.filter(service =>
          service.category && categories[0].title &&
          service.category.toLowerCase().trim() === categories[0].title.toLowerCase().trim()
        );

        if (firstTabCourses.length > 0) {
          setSelectedCourse(firstTabCourses[0]);
        }
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load services data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle tab change
  const handleTabChange = (key) => {
    setActiveTab(key);

    // Find the group for this tab
    const selectedGroup = serviceGroups.find(group => group.id === key);
    if (selectedGroup) {
      // Find courses for this group
      const groupCourses = servicesData.filter(service =>
        service.category && selectedGroup.title &&
        service.category.toLowerCase().trim() === selectedGroup.title.toLowerCase().trim()
      );

      // Select first course in the group
      if (groupCourses.length > 0) {
        setSelectedCourse(groupCourses[0]);
      } else {
        setSelectedCourse(null);
      }
    }
  };

  // Handle course selection
  const handleCourseSelect = (course) => {
    setSelectedCourse(course);
    if (isMobile) {
      setMobileDetailVisible(true);
    }
  };

  // Handle booking
  const handleBookNow = (service) => {
    if (!currentUser) {
      const serializableService = {
        ...service,
        icon: undefined
      };
      sessionStorage.setItem('intendedServiceBooking', JSON.stringify(serializableService));
      navigate('/signin');
      return;
    }

    const cleanService = service ? {
      ...service,
      icon: undefined
    } : null;

    navigate('/booking', {
      state: {
        service: cleanService,
        selectedServices: cleanService ? [cleanService] : []
      }
    });
  };

  // Course List Item Component
  const CourseListItem = ({ service, isSelected, onSelect }) => (
    <div
      style={{
        background: isSelected ? themeStyles.selectedBg : themeStyles.listItemBg,
        border: `2px solid ${isSelected ? themeStyles.selectedBorder : themeStyles.cardBorder}`,
        borderRadius: '16px',
        padding: isMobile ? '12px' : '20px',
        marginBottom: isMobile ? '6px' : '12px',
        cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: isSelected
          ? `0 8px 32px ${themeStyles.shadowColor}`
          : `0 4px 16px ${themeStyles.shadowColor}`,
        transform: isSelected ? 'translateY(-2px)' : 'translateY(0)',
        minHeight: isMobile ? 'auto' : '160px',
        height: 'auto',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
      }}
      onClick={() => onSelect(service)}
      onMouseEnter={(e) => {
        if (!isSelected) {
          e.currentTarget.style.background = themeStyles.listItemHover;
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = `0 8px 32px ${themeStyles.shadowColor}`;
        }
      }}
      onMouseLeave={(e) => {
        if (!isSelected) {
          e.currentTarget.style.background = themeStyles.listItemBg;
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = `0 4px 16px ${themeStyles.shadowColor}`;
        }
      }}
    >
      {/* Header Section - Professional Layout */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '14px',
        marginBottom: isMobile ? '10px' : '14px',
        height: isMobile ? 'auto' : '56px',
        minHeight: isMobile ? 'auto' : '56px'
      }}>
        <div style={{
          padding: '10px',
          borderRadius: '12px',
          background: themeStyles.gradientPrimary,
          color: '#1f2937',
          fontSize: '18px',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '40px',
          height: '40px',
          boxShadow: `0 6px 16px ${themeStyles.shadowColor}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
          {service.icon}
        </div>

        <div style={{ flex: 1, minWidth: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Title Section */}
          <div style={{
            marginBottom: '6px'
          }}>
            <Title level={5} style={{
              color: themeStyles.textPrimary,
              margin: 0,
              marginBottom: '2px',
              fontSize: isMobile ? '19px' : '13px',
              fontWeight: '700',
              lineHeight: '1.2',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxHeight: isMobile ? '46px' : '31px' // Adjusted for larger font
            }}>
              {service.title}
            </Title>
          </div>

          {/* Duration and Badge Row */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '6px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              flex: 1,
              minWidth: '0'
            }}>
              <CalendarOutlined style={{
                color: themeStyles.textSecondary,
                fontSize: '10px',
                flexShrink: 0
              }} />
              <Text style={{
                color: themeStyles.textSecondary,
                fontSize: '10px',
                fontWeight: '500',
                lineHeight: '1.1',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {service.duration}
              </Text>
            </div>
            <Badge
              count={service.category}
              style={{
                background: themeStyles.gradientPrimary,
                fontSize: '8px',
                height: '14px',
                lineHeight: '14px',
                border: 'none',
                color: '#1f2937',
                fontWeight: '600',
                borderRadius: '7px',
                padding: '0 4px',
                flexShrink: 0,
                maxWidth: '80px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            />
          </div>
        </div>
      </div>

      {/* Description Section - Exactly 3 Lines */}
      <div style={{
        flex: 1,
        minHeight: '0',
        height: isMobile ? 'auto' : '84px', // Auto height for mobile
        overflow: 'hidden'
      }}>
        <Text style={{
          color: themeStyles.textSecondary,
          fontSize: isMobile ? '13px' : '11px',
          lineHeight: '1.45', // Better line height for readability
          display: '-webkit-box',
          WebkitLineClamp: 3, // Exactly 3 lines
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          margin: 0,
          fontWeight: '400',
          letterSpacing: '0.005em',
          wordBreak: 'break-word',
          hyphens: 'auto',
          height: '100%',
          maxHeight: isMobile ? '56px' : '48px'
        }}>
          {service.description}
        </Text>
      </div>
    </div>
  );

  // Course Details Component
  const CourseDetailsPanel = ({ service }) => {
    const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

    // Reset expanded state when service changes
    useEffect(() => {
      setIsDescriptionExpanded(false);
    }, [service?.id]);

    if (!service) {
      return (
        <div style={{
          background: themeStyles.cardBg,
          border: `1px solid ${themeStyles.cardBorder}`,
          borderRadius: '24px',
          padding: '48px',
          height: '500px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: `0 8px 32px ${themeStyles.shadowColor}`,
        }}>
          <Empty
            description={
              <Text style={{ color: themeStyles.textSecondary, fontSize: '16px' }}>
                Select a course to view details
              </Text>
            }
          />
        </div>
      );
    }

    // Check if description needs truncation (rough estimation)
    const needsTruncation = service.description && service.description.length > 150;

    return (
      <div style={{
        background: themeStyles.cardBg,
        border: isMobile ? 'none' : `1px solid ${themeStyles.cardBorder}`,
        borderRadius: '24px', // Always rounded
        padding: isMobile ? '24px 0' : '32px', // Removing horizontal padding for mobile
        minHeight: 'auto', // Fit content
        boxShadow: isMobile ? 'none' : `0 8px 32px ${themeStyles.shadowColor}`,
        backdropFilter: 'blur(20px)',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'flex-start', // Always start from left edge
          gap: isMobile ? '8px' : '20px',
          marginBottom: isMobile ? '12px' : '32px',
          textAlign: 'left', // Always left aligned
          padding: isMobile ? '0 12px' : '0' // Slight padding for text safety if needed, or 0 if strictly edge. User said "remove it". I'll use a tiny 4px or just relies on the container being 0. Let's start with 0 container and maybe adds padding to children if they touch the literal border?
          // Actually, if I put padding 0 on parent, I should probably put a tiny padding on the children so it doesn't look broken.
          // But user said "remove it".
          // I'll stick to 0 on container, and add `padding: '0 8px'` to children?
          // "text should start from edge". Providing 8px is safe.
        }}>
          {!isMobile && (
            <div style={{
              padding: '20px',
              borderRadius: '18px',
              background: themeStyles.gradientPrimary,
              color: 'white',
              fontSize: '32px',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 6px 24px ${themeStyles.shadowColor}`,
            }}>
              {service.icon}
            </div>
          )}
          <div style={{
            flex: 1,
            width: isMobile ? '100%' : 'auto',
            padding: isMobile ? '0 8px' : '0' // Adding minimal 8px padding so it's not literally on the border line
          }}>
            <Title level={2} style={{
              color: themeStyles.textPrimary,
              margin: isMobile ? '0 0 4px 0' : '0 0 16px 0',
              fontSize: isMobile ? '18px' : '32px',
              fontWeight: '800',
              letterSpacing: '-0.02em',
              lineHeight: '1.2',
              wordBreak: 'normal',
              overflowWrap: 'anywhere',
              background: themeStyles.gradientPrimary,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              {service.title}
            </Title>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start', // Left align
              gap: '16px',
              marginBottom: isMobile ? '8px' : '16px',
              flexWrap: 'wrap'
            }}>
              {/* Duration only on mobile if space is tight, or simplified */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Text style={{ color: themeStyles.textSecondary, fontSize: '13px', fontWeight: '500' }}>
                  Duration: {service.duration}
                </Text>
              </div>
            </div>
          </div>
        </div>

        {/* Description with See More/Less */}
        <div style={{ marginBottom: isMobile ? '12px' : '32px' }}>
          <div style={{ position: 'relative' }}>
            <div style={{
              color: themeStyles.textSecondary,
              fontSize: isMobile ? '13px' : '16px',
              lineHeight: '1.5',
              margin: 0,
              fontWeight: '400',
              textAlign: 'left', // Left align
              padding: isMobile ? '0 8px' : '0', // Minimal padding
              ...(isDescriptionExpanded ? {
                maxHeight: 'none'
              } : {
                display: '-webkit-box',
                WebkitLineClamp: 5,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                maxHeight: '128px',
                textOverflow: 'ellipsis'
              })
            }}>
              {service.description}
            </div>

            {needsTruncation && (
              <Button
                type="link"
                onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                style={{
                  padding: '4px 0',
                  height: 'auto',
                  color: themeStyles.priceColor,
                  fontWeight: '600',
                  fontSize: '12px',
                  marginTop: '4px',
                  border: 'none',
                  background: 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%'
                }}
              >
                {isDescriptionExpanded ? 'Read Less' : 'More Information...'}
              </Button>
            )}
          </div>
        </div>

        {/* Features */}
        <div style={{ marginBottom: isMobile ? '16px' : '32px' }}>
          <Text strong style={{
            color: themeStyles.textPrimary,
            display: 'block',
            marginBottom: isMobile ? '4px' : '20px',
            fontSize: isMobile ? '14px' : '18px',
          }}>
            What's Included:
          </Text>
          <div style={{
            display: isMobile ? 'flex' : 'grid',
            flexDirection: 'column',
            gap: isMobile ? '2px' : '12px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))'
          }}>
            {service.includes.map((item, index) => (
              <div key={index} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: isMobile ? '2px 8px' : '16px', // 8px side padding
                background: isMobile ? 'transparent' : (theme === 'dark'
                  ? 'rgba(67, 127, 151, 0.08)'
                  : 'rgba(238, 225, 179, 0.08)'),
                borderRadius: isMobile ? '0' : '16px',
                border: isMobile ? 'none' : `1px solid ${theme === 'dark' ? 'rgba(67, 127, 151, 0.15)' : 'rgba(238, 225, 179, 0.15)'}`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}>
                <CheckCircleOutlined style={{
                  color: themeStyles.priceColor,
                  fontSize: isMobile ? '14px' : '18px',
                  flexShrink: 0
                }} />
                <Text style={{
                  color: themeStyles.textSecondary,
                  fontSize: isMobile ? '13px' : '15px',
                  lineHeight: '1.4',
                  fontWeight: '500',
                }}>
                  {item}
                </Text>
              </div>
            ))}
          </div>
        </div>

        {/* Pricing */}
        <div style={{ marginBottom: '16px' }}>
          {isMobile ? (
            // Simple row for mobile
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '12px',
              padding: isMobile ? '0 8px' : '0' // 8px side padding
            }}>
              <div>
                <Text strong style={{ display: 'block', color: themeStyles.textPrimary, fontSize: '14px' }}>Pricing:</Text>
                <Text style={{ color: themeStyles.textSecondary, fontSize: '13px' }}>
                  {service.options[0]?.type || 'Complete Package'}
                </Text>
              </div>
              <Text strong style={{ color: themeStyles.priceColor, fontSize: '18px', fontWeight: 'bold' }}>
                ₹{service.options[0]?.price}
              </Text>
            </div>
          ) : (
            // Original card for desktop
            <>
              <Title level={4} style={{
                color: themeStyles.textPrimary,
                marginBottom: '20px',
                fontSize: '18px',
                fontWeight: '700',
                letterSpacing: '-0.01em'
              }}>
                Investment Options
              </Title>
              <div style={{
                background: theme === 'dark'
                  ? 'rgba(67, 127, 151, 0.15)'
                  : 'rgba(238, 225, 179, 0.25)',
                borderRadius: '24px',
                padding: '28px',
                border: `2px solid ${theme === 'dark' ? 'rgba(67, 127, 151, 0.3)' : 'rgba(238, 225, 179, 0.4)'}`,
                boxShadow: `0 8px 32px ${themeStyles.shadowColor}`,
                position: 'relative',
                overflow: 'hidden'
              }}>
                {service.options.map((option, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '20px',
                    marginBottom: index < service.options.length - 1 ? '16px' : '0',
                    background: themeStyles.cardBg,
                    borderRadius: '16px',
                    border: `1px solid ${themeStyles.cardBorder}`,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'default',
                    flexWrap: 'wrap',
                    gap: '12px'
                  }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = `0 8px 24px ${themeStyles.shadowColor}`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div>
                      <Text strong style={{
                        color: themeStyles.textPrimary,
                        fontSize: '16px',
                        display: 'block',
                        fontWeight: '700',
                        marginBottom: '4px',
                        letterSpacing: '-0.01em'
                      }}>
                        {option.type}
                      </Text>
                      <Text style={{
                        color: themeStyles.textSecondary,
                        fontSize: '14px',
                        fontWeight: '500'
                      }}>
                        Duration: {option.duration}
                      </Text>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <Text strong style={{
                        color: themeStyles.priceColor,
                        fontSize: '24px',
                        fontWeight: '800',
                        display: 'block',
                        lineHeight: '1',
                        letterSpacing: '-0.02em'
                      }}>
                        ₹{option.price}
                      </Text>
                      <Text style={{
                        color: themeStyles.textSecondary,
                        fontSize: '12px',
                        opacity: 0.8,
                        fontWeight: '500'
                      }}>
                        per {option.duration.toLowerCase()}
                      </Text>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
          {/* Close button removed for mobile as per request */}
          <Button
            type="primary"
            block // Full width on mobile
            size="large"
            style={{
              flex: 1,
              background: themeStyles.gradientPrimary,
              border: 'none',
              borderRadius: '16px',
              height: isMobile ? '56px' : '64px',
              fontWeight: '700',
              fontSize: '18px',
              color: '#1f2937',
              boxShadow: `0 8px 32px ${themeStyles.shadowColor}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
            icon={<InfoCircleOutlined style={{ fontSize: '20px' }} />}
            onClick={() => handleBookNow(service)}
          >
            {isMobile ? 'Book Now' : 'Book This Program'}
          </Button>
        </div>
      </div>
    );
  };

  // Tab Content Component
  const TabContent = ({ groupServices }) => {
    return (
      <Row gutter={isMobile ? [12, 12] : [24, 24]}>
        {/* Left Panel - Course List */}
        <Col xs={24} lg={10}>
          <div style={{
            background: themeStyles.cardBg,
            border: `1px solid ${themeStyles.cardBorder}`,
            borderRadius: '24px',
            padding: isMobile ? '12px' : '28px',
            maxHeight: '700px',
            overflowY: 'auto',
            boxShadow: `0 8px 32px ${themeStyles.shadowColor}`,
          }}>
            <Title level={3} style={{
              color: themeStyles.textPrimary,
              marginBottom: isMobile ? '16px' : '28px',
              fontSize: isMobile ? '18px' : '22px',
              fontWeight: '800',
              background: themeStyles.gradientPrimary,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>
              Available Programs
            </Title>
            {groupServices.map((service) => (
              <CourseListItem
                key={service.id}
                service={service}
                isSelected={selectedCourse?.id === service.id}
                onSelect={handleCourseSelect}
              />
            ))}
          </div>
        </Col>

        {/* Right Panel - Course Details (Desktop only) */}
        {!isMobile && (
          <Col xs={24} lg={14}>
            <CourseDetailsPanel service={selectedCourse} />
          </Col>
        )}
      </Row>
    );
  };

  // Create tab items
  const tabItems = serviceGroups.map(group => {
    const groupServices = servicesData.filter(service =>
      service.category && group.title &&
      service.category.toLowerCase().trim() === group.title.toLowerCase().trim()
    );

    return {
      key: group.id,
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {getTabIcon(group.id)}
          <span>{group.title}</span>
          <Badge
            count={groupServices.length}
            style={{
              background: themeStyles.gradientPrimary,
              fontSize: '10px',
              border: 'none',
              color: '#1f2937'
            }}
          />
        </div>
      ),
      children: <TabContent groupServices={groupServices} />
    };
  });

  if (loading) {
    return (
      <div style={{
        background: themeStyles.background,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        background: themeStyles.background,
        minHeight: '100vh',
      }}>
        <ServicesHero theme={theme} />
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: '40px 20px'
        }}>
          <Alert
            message="Error Loading Services"
            description={error}
            type="error"
            showIcon
            action={
              <Button size="small" danger onClick={loadData}>
                Try Again
              </Button>
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div style={{
      background: themeStyles.background,
      minHeight: '100vh',
    }}>
      {/* Hero Section */}
      <ServicesHero theme={theme} />

      {/* Main Content */}
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '0 20px 80px'
      }}>
        <div style={{
          background: themeStyles.containerBg,
          borderRadius: '32px',
          padding: isMobile ? '16px' : '40px',
          border: `1px solid ${themeStyles.cardBorder}`,
          boxShadow: `0 24px 80px ${themeStyles.shadowColor}`,
        }}>
          {serviceGroups.length > 0 ? (
            isMobile ? (
              <div style={{ marginBottom: '16px' }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '8px',
                  marginBottom: '16px'
                }}>
                  {serviceGroups.map(group => {
                    const isActive = activeTab === group.id;
                    return (
                      <div
                        key={group.id}
                        onClick={() => handleTabChange(group.id)}
                        style={{
                          background: isActive ? themeStyles.gradientPrimary : themeStyles.cardBg,
                          border: isActive ? 'none' : `1px solid ${themeStyles.cardBorder}`,
                          borderRadius: '16px',
                          padding: '8px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '2px',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          boxShadow: isActive ? `0 8px 24px ${themeStyles.shadowColor}` : 'none',
                          height: '70px',
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                      >
                        <div style={{
                          fontSize: '24px',
                          color: isActive ? '#1f2937' : themeStyles.textPrimary,
                          transition: 'all 0.3s ease'
                        }}>
                          {getTabIcon(group.id)}
                        </div>
                        <Text strong style={{
                          color: isActive ? '#1f2937' : themeStyles.textPrimary,
                          fontSize: '12px',
                          textAlign: 'center',
                          lineHeight: '1.2'
                        }}>
                          {group.title}
                        </Text>
                      </div>
                    );
                  })}
                </div>

                {/* Manually render the content for the selected tab */}
                <div>
                  {tabItems.find(item => item.key === activeTab)?.children}
                </div>
              </div>
            ) : (
              <Tabs
                activeKey={activeTab}
                onChange={handleTabChange}
                items={tabItems}
                className={`services-tabs ${theme === 'dark' ? 'dark-tabs' : 'light-tabs'}`}
                tabBarStyle={{
                  background: themeStyles.cardBg,
                  borderRadius: '20px',
                  padding: '12px',
                  marginBottom: '40px',
                  border: `1px solid ${themeStyles.cardBorder}`,
                  boxShadow: `0 8px 32px ${themeStyles.shadowColor}`,
                }}
                size="large"
                centered
              />
            )
          ) : (
            <Empty description="No services available" />
          )}
        </div>
      </div>

      {/* Call to Action */}
      <CallToAction theme={theme} />

      {/* Mobile Service Details Modal */}
      <Modal
        open={mobileDetailVisible && isMobile}
        onCancel={() => setMobileDetailVisible(false)}
        footer={null}
        centered
        width="calc(100% - 32px)"
        bodyStyle={{ padding: 0 }}
        closeIcon={
          <div style={{
            position: 'absolute',
            right: '16px',
            top: '16px',
            color: themeStyles.textSecondary,
            fontSize: '20px',
            padding: '8px',
            cursor: 'pointer',
            zIndex: 10
          }}>
            <span style={{ fontSize: '24px', lineHeight: 1 }}>×</span>
          </div>
        }
      >
        <div style={{
          padding: 0,
          background: themeStyles.background,
          borderRadius: '24px',
          overflow: 'hidden'
        }}>
          {selectedCourse && <CourseDetailsPanel service={selectedCourse} />}
        </div>
      </Modal>
    </div>
  );
};

export default Services;